import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Table, Input, Badge, Button } from 'reactstrap'
import LoadingSpinner from '../../Shared/LoadingSpinner'
import { fetchTenantSubscriptions } from '../../actions/cspRenewalsActions'

const PAGE_SIZE = 25
const ALL_OPTION = '__all__'

const formatRenewal = (iso) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

// DD reports raw ISO 8601 term durations ("P1Y" / "P1M" / "P3Y") and free-text
// billing frequencies ("Monthly" / "Annual"). Combine into a single readable
// cell — "Annual / Monthly billing" reads better than two narrow columns and
// fits the size="md" modal.
const formatTerm = (termDuration, billingFrequency) => {
  const termLabel = termDuration === 'P1Y' ? 'Annual'
    : termDuration === 'P1M' ? 'Monthly'
    : termDuration === 'P3Y' ? '3-Year'
    : termDuration || '—'
  if (!billingFrequency || billingFrequency.toLowerCase() === termLabel.toLowerCase()) {
    return termLabel
  }
  return `${termLabel} / ${billingFrequency} billing`
}

// Map raw DD status to a coloured badge — ACTIVE green, REMOVED red, anything
// else (SUSPENDED, UNKNOWN, transitional states) secondary so unrecognised
// values still render readably instead of being invisible.
const StatusBadge = ({ status }) => {
  const upper = (status || '').toUpperCase()
  const color = upper === 'ACTIVE' ? 'success'
    : upper === 'REMOVED' ? 'danger'
    : 'secondary'
  return <Badge color={color}>{status || '—'}</Badge>
}

const TenantSubscriptionsTab = ({ isOpen, tenantId }) => {
  const dispatch = useDispatch()
  const [subs, setSubs] = useState(null) // null = loading, [] = empty
  const [statusFilter, setStatusFilter] = useState('ACTIVE')
  const [page, setPage] = useState(1)

  // Fetch on tab open + whenever tenant changes. Re-fetches on every open
  // (no caching) so admin sees fresh data after a sync — the response is
  // small enough that a stale cache isn't worth the complexity.
  useEffect(() => {
    if (!isOpen || !tenantId) return
    let cancelled = false
    setSubs(null)
    setPage(1)
    setStatusFilter('ACTIVE')
    dispatch(fetchTenantSubscriptions(tenantId)).then((rows) => {
      if (!cancelled) setSubs(rows || [])
    })
    return () => { cancelled = true }
  }, [isOpen, tenantId, dispatch])

  // Build the status filter options from the data so DD-emitted statuses
  // we didn't know about at write-time (new statuses, regional variants)
  // still appear instead of being invisible. Sorted alphabetically with
  // ACTIVE pinned first since it's the operational default.
  const statusOptions = useMemo(() => {
    if (!subs) return []
    const distinct = Array.from(new Set(
      subs.map((s) => (s.status || '').toUpperCase()).filter(Boolean)
    ))
    distinct.sort((a, b) => a.localeCompare(b))
    const ordered = distinct.includes('ACTIVE')
      ? ['ACTIVE', ...distinct.filter((s) => s !== 'ACTIVE')]
      : distinct
    return ordered
  }, [subs])

  const filtered = useMemo(() => {
    if (!subs) return []
    if (statusFilter === ALL_OPTION) return subs
    return subs.filter((s) => (s.status || '').toUpperCase() === statusFilter)
  }, [subs, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  // Page can fall out of range when the filter narrows the list — clamp on
  // render so the table never shows an empty page when results exist.
  const safePage = Math.min(page, totalPages)
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const startIdx = filtered.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(safePage * PAGE_SIZE, filtered.length)

  const onFilterChange = (value) => {
    setStatusFilter(value)
    setPage(1)
  }

  if (subs === null) {
    return (
      <div className="text-center py-4">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex align-items-center mb-2">
        <span className="small text-muted mr-2">Status</span>
        <Input
          type="select"
          bsSize="sm"
          value={statusFilter}
          onChange={(e) => onFilterChange(e.target.value)}
          style={{ width: 'auto' }}
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
          <option value={ALL_OPTION}>All ({subs.length})</option>
        </Input>
        <span className="small text-muted ml-3">
          {filtered.length === subs.length
            ? `${subs.length} subscriptions`
            : `${filtered.length} of ${subs.length} subscriptions`}
        </span>
      </div>

      {filtered.length === 0 ? (
        <p className="text-muted small">
          {subs.length === 0
            ? 'No subscriptions on file for this tenant.'
            : `No subscriptions match the ${statusFilter === ALL_OPTION ? 'current' : statusFilter} filter.`}
        </p>
      ) : (
        <>
          <Table size="sm" bordered className="mb-2">
            <thead>
              <tr>
                <th>Subscription</th>
                <th style={{ width: '70px', textAlign: 'right' }}>Qty</th>
                <th style={{ width: '100px' }}>Status</th>
                <th style={{ width: '170px' }}>Term</th>
                <th style={{ width: '120px' }}>Renewal</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((s) => (
                <tr key={s.id}>
                  <td>{s.subscriptionName || <em className="text-muted">(no name)</em>}</td>
                  <td style={{ textAlign: 'right' }}>{s.quantity}</td>
                  <td><StatusBadge status={s.status} /></td>
                  <td>{formatTerm(s.termDuration, s.billingFrequency)}</td>
                  <td>{formatRenewal(s.renewalDate)}</td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Pagination footer always rendered (even at single-page) so the
              control surface is consistently visible. Buttons disable when
              not applicable; "Showing A-B of N" gives admin a sense of
              scale even before they hit the limit. */}
          <div className="d-flex align-items-center justify-content-between small">
            <span className="text-muted">
              Showing {startIdx}-{endIdx} of {filtered.length}
            </span>
            <div>
              <span className="text-muted mr-3">
                Page {safePage} of {totalPages}
              </span>
              <Button
                size="sm"
                color="outline-secondary"
                className="mr-2"
                disabled={safePage <= 1}
                onClick={() => setPage(safePage - 1)}
              >
                ‹ Prev
              </Button>
              <Button
                size="sm"
                color="outline-secondary"
                disabled={safePage >= totalPages}
                onClick={() => setPage(safePage + 1)}
              >
                Next ›
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default TenantSubscriptionsTab
